import React, { useEffect, useState } from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import makeStyles from '@mui/styles/makeStyles';
import CircularProgress from '@mui/material/CircularProgress';
import logo from '../../../SaigeLogo.svg';

import { loginUser } from '../../../api/serverRequests';
import { useUserState } from '../../../hook/customerHook';
import useAnalyticsEventTracker from '../../../hook/analyticsHook';
import { Copyright } from '../../';

import moment from 'moment';
import { green, lightGreen } from '@mui/material/colors';
import { BorderColor } from '@mui/icons-material';
import Image from '../../../assets/images/Login_Page_Image.png';
import { saveUserLocation } from '../../../helper/parser';

const useStyles = makeStyles((theme) => ({
	root: {
		height: '100vh'
	},
	image: {
		backgroundImage: `url(${Image})`,
		backgroundRepeat: 'no-repeat',
		backgroundColor:
			theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
		backgroundSize: 'cover',
		backgroundPosition: 'center'
	},
	paper: {
		padding: theme.spacing(15, 10),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		height: '100%',
		width: '100%',
		backgroundColor: theme.palette.grey[0]
	},
	signUp: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center'
	},

	formControl: {
		marginTop: theme.spacing(1),
		width: '100%',
		'& label.Mui-focused': {
			color: '#004F2B',
			background: 'rgba(72, 193, 198, 0.06)'
		},
		'& .MuiInput-underline:after': {
			borderBottomColor: '#004F2B'
		},
		'& .MuiOutlinedInput-root': {
			'& fieldset': {
				borderColor: '#004F2B'
			},
			'&:hover fieldset': {
				borderColor: '#004F2B'
			},
			'&.Mui-focused fieldset': {
				borderColor: '#004F2B'
			}
		}
	},
	form: {
		width: '100%', // Fix IE 11 issue.
		marginTop: theme.spacing(1),
		alignItems: 'center'
	},
	submit: {
		margin: theme.spacing(0, 0, 2),
		backgroundColor: '#004F2B',
		boxShadow: 'none'
	},
	linkContent: {
		display: 'flex',
		flexDirection: 'column'
	},
	forgotPass: {
		marginLeft: 'auto',
		marginRight: '0',
		color: '#004F2B',
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(2)
	},
	signUpText: {
		color: 'black'
	},
	signUpLink: {
		color: 'blue'
	},
	textInput: {
		'& label.Mui-focused': {
			color: '#004F2B',
			background: 'rgba(72, 193, 198, 0.06)'
		},
		'& .MuiInput-underline:after': {
			borderBottomColor: '#004F2B'
		},
		'& .MuiOutlinedInput-root': {
			'& fieldset': {
				borderColor: '#004F2B'
			},
			'&:hover fieldset': {
				borderColor: '#004F2B'
			},
			'&.Mui-focused fieldset': {
				borderColor: '#004F2B'
			}
		}
	},

	copyRight: {
		alignSelf: 'end',
		color: 'blue'
	},
	logoImg: {
		height: '80px',
		width: '135px',
		marginBottom: theme.spacing(2)
	},
	title: {
		position: 'absolute',
		[theme.breakpoints.down('md')]: {
			display: 'none'
		},
		color: '#FF9900',
		marginTop: theme.spacing(6),
		marginLeft: theme.spacing(7),
		textTransform: 'uppercase',
		fontStyle: 'normal',
		fontWeight: '600',
		fontSize: '28px',
		lineHeight: '110%',
		width: '350px',
		height: '84px',
		transform: 'scale(1.05, 1)'
	},
	subTitle: {
		position: 'absolute',
		[theme.breakpoints.down('md')]: {
			display: 'none'
		},
		width: '350px',
		height: '84px',
		marginTop: theme.spacing(14.25),
		marginLeft: theme.spacing(7),
		fontStyle: 'normal',
		fontWeight: '500',
		fontSize: '13px',
		lineHeight: '125%',
		color: '#1D2024',
		transform: 'scale(1.05, 1)'
	},
	accountCreated: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		background:
			'linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #2E7D32',
		width: '410px',
		height: '48px',
		allign: 'center'
	},
	accountCreatedText: {
		marginTop: theme.spacing(1.5),
		allign: 'center',
		fontStyle: 'normal',
		fontWeight: '400'
	},
	loadingOverlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		backgroundColor: 'rgba(255, 255, 255, 0.7)',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 9999
	}
}));

export default function Login(props) {
	const history = useHistory();
	const methods = useForm();
	const {
		handleSubmit,
		control,
		formState: { errors },
		watch
	} = methods;
	const [state, setState] = React.useState({
		checked: true
	});
	const location = useLocation();
	const [isLoading, setIsLoading] = useState(false);

	// Watch email and password fields individually
	const watchedEmail = watch('email');
	const watchedPassword = watch('password');

	const isEmailFilled = watchedEmail && watchedEmail.trim() !== '';
	const isPasswordFilled = watchedPassword && watchedPassword.trim() !== '';
	const isFormFilled = isEmailFilled && isPasswordFilled;

	const [userState, dispatchUser] = useUserState();
	const [errMessage, setErrMessage] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [showAccountCreated, setShowAccountCreated] = useState(false);

	const gaEventTracker = useAnalyticsEventTracker('Login');

	const classes = useStyles();

	const handleChange = (event) => {
		setState({ ...state, [event.target.name]: event.target.checked });
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const onSubmit = async (formData) => {
		setIsLoading(true);
		loginUser(formData)
			.then((res) => {
				if (res.data.success) {
					const user = res.data.user;
					gaEventTracker('User logged in', user.user_id);
					dispatchUser({
						isLoggedIn: true,
						user: user,
						lastActivity: moment().format(),
						Remember: formData.remember,
						advFilters: { dwellingTypes: 'detached', mlsSearch: true }
					});
					if (location.state?.from) {
						history.push(location.state.from, { from: '/login' });
					} else {
						history.push('/user/search', { from: '/login' });
					}
				} else {
					setErrMessage(res.data.err);
					setIsLoading(false);
				}
			})
			.catch((err) => {
				setErrMessage(err.message);
				console.log(err.message);
				setIsLoading(false);
			});
	};

	// This function allows to ask for user's permission to provide their location"
	// useEffect(() => {
	// 	saveUserLocation();
	// }, []);
	return (
		<Grid container component="main" className={classes.root}>
			<CssBaseline />
			{isLoading && (
				<div className={classes.loadingOverlay}>
					<CircularProgress size={60} />
				</div>
			)}
			<Grid item xs={false} sm={2} md={6} className={classes.image} />
			<Typography variant="h3" className={classes.title}>
				The #1 property discovery platform
			</Typography>
			<Typography variant="p" className={classes.subTitle}>
				Use advanced AI to create personalized marketing, capture seller leads, and track
				engagement with property-specific data.
			</Typography>
			<Grid item xs={12} sm={8} md={6} component={Paper} elevation={6} square>
				<div className={classes.paper}>
					{props.location.state === false && (
						<div className={classes.accountCreated}>
							<Typography className={classes.accountCreatedText} variant="p">
								Account Created
							</Typography>
						</div>
					)}

					<img src={logo} alt="Logo" className={classes.logoImg} />

					<form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
						{errMessage && <p className="text-danger my-0">{errMessage}</p>}
						<Controller
							name="email"
							defaultValue=""
							control={control}
							render={({ field }) => (
								<TextField
									className={classes.textInput}
									variant="outlined"
									margin="normal"
									fullWidth
									id="email"
									label="Email Address"
									name="email"
									autoComplete="email"
									autoFocus
									{...field}
								/>
							)}
							rules={{
								required: 'Email is required',
								pattern: {
									value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
									message: 'Invalid email address'
								}
							}}
						/>
						<ErrorMessage
							errors={errors}
							name="email"
							render={({ message }) => <p className="text-danger mx-2">{message}</p>}
						/>

						<Controller
							name="password"
							defaultValue=""
							control={control}
							render={({ field }) => (
								<TextField
									className={classes.textInput}
									variant="outlined"
									margin="normal"
									fullWidth
									name="password"
									id="password"
									label="Password"
									type={showPassword ? 'text' : 'password'}
									autoComplete="current-password"
									{...field}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													aria-label="toggle password visibility"
													onClick={handleClickShowPassword}
													edge="end"
													size="large"
												>
													{showPassword ? (
														<Visibility />
													) : (
														<VisibilityOff />
													)}
												</IconButton>
											</InputAdornment>
										)
									}}
								/>
							)}
							rules={{
								required: 'Password is required',
								minLength: {
									value: 8,
									message: 'Minimum length of password is 8 characters!'
								},
								pattern: {
									value: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-.,)(:";'\+_=]).{8,}$/,
									message:
										"A password must contain at least eight characters, including at least one number, both lower and uppercase letters, and special characters of '#?!@$%^&*-'"
								}
							}}
						/>
						<ErrorMessage
							errors={errors}
							name="password"
							render={({ message }) => <p className="text-danger mx-2">{message}</p>}
						/>
						<div className={classes.linkContent}>
							<Link
								href="/reset-password"
								variant="body2"
								className={classes.forgotPass}
							>
								Forgot password?
							</Link>
							<FormControlLabel
								control={
									<Controller
										name="remember"
										defaultValue={true}
										control={control}
										render={({ field }) => (
											<Checkbox
												checked={field.value}
												onChange={(e) => field.onChange(e.target.checked)}
												color="primary"
											/>
										)}
									/>
								}
								label={
									<Box component="div" color="black" fontSize={14}>
										Remember Me
									</Box>
								}
							/>
						</div>
						<Button
							type="submit"
							fullWidth
							variant="contained"
							color="primary"
							className={classes.submit}
							disabled={!isFormFilled}
						>
							Sign In
						</Button>

						<div className={classes.signUp}>
							<p className={classes.signUpText}>
								Don't have an account?
								<Link
									className={classes.signUpLink}
									href="mailto:info@realsaige.com?subject=RE: App Access"
									variant="body2"
								>
									{' '}
									Sign up now!
								</Link>
							</p>
						</div>

						<Box mt={5}>
							<Copyright />
						</Box>
					</form>
				</div>
			</Grid>
		</Grid>
	);
}
